import { useEffect, useState } from 'react'
import { onDemoSessionEnded } from '../demoSession.js'
import { useQuery } from '@tanstack/react-query'
import type { AccountsAdminApi } from '../adminApi.js'
import { DemoPeopleModal, DemoRoleButton } from '../components/DemoPeopleModal.js'
import { useDemoSessionLauncher } from '../components/useDemoSessionLauncher.js'

/** The admin calls this screen uses. */
type AdminApi = AccountsAdminApi

interface OpenAsMenuProps {
  api: AdminApi
  clientId: string
  organizationName?: string
  /** Icon only, for a table row; otherwise the icon with its label. */
  compact?: boolean
}

/**
 * The admin's way into a demo client as any of its people. The button
 * opens the chooser; choosing someone opens a new tab signed in as them,
 * through `POST /admin/clients/{clientId}/demo-sessions`. The API refuses
 * it for anything but a demo client, so this is shown for demo clients
 * only, and the people load when the chooser is opened, not for every row.
 */
export function OpenAsMenu({ api, clientId, organizationName, compact = false }: OpenAsMenuProps) {
  const [isOpen, setIsOpen] = useState(false)
  const peopleQuery = useQuery({ queryKey: ['admin', 'demo-people', clientId], queryFn: () => api.listDemoPeople(clientId), enabled: isOpen })
  const { launch, pending, error } = useDemoSessionLauncher((input) => api.openDemoSession(clientId, input))
  // The chooser has done its job once a session it opened has ended: close it, so coming back from the demo is not to a dialog still asking who to open.
  useEffect(() => (isOpen ? onDemoSessionEnded(true, () => setIsOpen(false)) : undefined), [isOpen])

  return (
    <span onClick={(event) => event.stopPropagation()}>
      <DemoRoleButton label="Open as…" showLabel={!compact} onClick={() => setIsOpen(true)} />
      {isOpen && (
        <DemoPeopleModal
          title={organizationName ? `Open ${organizationName} as…` : 'Open this demo as…'}
          subtitle="Choosing someone opens a new tab signed in as them."
          people={peopleQuery.data}
          loadWorkers={(facilityId) => api.listDemoWorkers(clientId, facilityId)}
          searchWorkers={(query) => api.searchDemoWorkers(clientId, query)}
          workersKey={clientId}
          isLoading={peopleQuery.isPending}
          loadError={peopleQuery.isError ? (peopleQuery.error instanceof Error ? peopleQuery.error.message : 'Could not load the people.') : null}
          onChoose={(input) => void launch(input)}
          pending={pending}
          error={error}
          onClose={() => setIsOpen(false)}
        />
      )}
    </span>
  )
}
